import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { X, Shield, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'rgpd_consent';
const CONSENT_VERSION = '1.0';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const consentData = JSON.parse(consent);
        // Check if consent version is current
        if (consentData.version !== CONSENT_VERSION) {
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const acceptAll = () => {
    const consentData = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      essential: true,
      analytics: false, // We don't use analytics
      preferences: true
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setShowBanner(false);
  };

  const acceptEssential = () => {
    const consentData = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      essential: true,
      analytics: false,
      preferences: false
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 flex-shrink-0">
            <Cookie className="w-6 h-6 text-blue-600" />
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-blue-600 sm:hidden" />
              <h3 className="font-semibold text-gray-900">
                Protection de vos données personnelles
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-3">
              Cette application utilise des cookies strictement nécessaires à son fonctionnement 
              (authentification, préférences). Aucun cookie tiers ou de tracking n'est utilisé.
              {' '}
              <Link to="/politique-confidentialite" className="text-blue-600 hover:underline">
                En savoir plus
              </Link>
            </p>

            {showDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <h4 className="font-medium mb-2">Détail des cookies utilisés :</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">Cookies essentiels</span>
                      <p className="text-xs text-gray-500">Authentification et sécurité (obligatoires)</p>
                    </div>
                    <span className="text-green-600 text-xs font-medium bg-green-100 px-2 py-1 rounded">
                      Toujours actifs
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">Cookies de préférence</span>
                      <p className="text-xs text-gray-500">Mémorisation de vos choix d'affichage</p>
                    </div>
                    <span className="text-blue-600 text-xs font-medium bg-blue-100 px-2 py-1 rounded">
                      Optionnels
                    </span>
                  </div>
                  <div className="flex items-center justify-between opacity-50">
                    <div>
                      <span className="font-medium">Cookies analytics</span>
                      <p className="text-xs text-gray-500">Statistiques d'utilisation</p>
                    </div>
                    <span className="text-gray-600 text-xs font-medium bg-gray-200 px-2 py-1 rounded">
                      Non utilisés
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={acceptAll} size="sm">
                Accepter tout
              </Button>
              <Button onClick={acceptEssential} variant="outline" size="sm">
                Essentiels uniquement
              </Button>
              <Button 
                onClick={() => setShowDetails(!showDetails)} 
                variant="ghost" 
                size="sm"
                className="text-gray-600"
              >
                {showDetails ? 'Masquer les détails' : 'Voir les détails'}
              </Button>
            </div>
          </div>

          {/* Close button */}
          <button 
            onClick={acceptEssential}
            className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function to check consent status
export function hasConsent(type = 'essential') {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;
    const consentData = JSON.parse(consent);
    return consentData[type] === true;
  } catch {
    return false;
  }
}

// Helper function to get consent data
export function getConsentData() {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return null;
    return JSON.parse(consent);
  } catch {
    return null;
  }
}

// Helper function to revoke consent
export function revokeConsent() {
  localStorage.removeItem(CONSENT_KEY);
}
